/**
 * De hulplijnen die onder je tekening liggen, in pixels.
 *
 * Niet in vectorlijnen. Een dunne schuine lijn over een raster van 32 bij 32 is
 * geen hulp: je ziet waar hij ongeveer loopt, maar niet welk vakje je moet
 * aanklikken. Deze module levert daarom vakjes op, zodat de hulp op precies
 * dezelfde maat staat als wat je erover tekent en je hem vakje voor vakje kunt
 * overtrekken.
 *
 * De schuinte is overal 2:1, twee vakjes opzij per vakje omlaag. Dat is de
 * schuinte waar pixelkunst isometrisch van wordt, en de enige die op hele vakjes
 * een gelijkmatige lijn geeft: twee naast elkaar, dan een stapje.
 *
 * **Alles is symmetrisch door de manier waarop het gemaakt wordt.** Er wordt maar
 * één helft uitgerekend, de rechter, en die wordt gespiegeld. Bresenham van links
 * naar rechts en van rechts naar links leveren namelijk nét niet hetzelfde op, en
 * dat zag je: de linkerrib van de kubus stond op een ander vakje dan de rechter.
 * Spiegelen kan dat niet gebeuren.
 *
 * De spiegelas ligt tussen de twee middelste kolommen: op een doek van 32 tussen
 * 15 en 16. Vandaar dat de punt van een ruit twee vakjes breed is, één aan elke
 * kant, precies zoals je een isometrische punt met de hand zou zetten.
 *
 * Deze module rekent alleen en weet niets van de app.
 */

/**
 * Eén kwart van een 2:1 lijn, vanaf de spiegelas naar buiten.
 *
 * Bij de as één vakje, en elke rij verder twee vakjes die twee opzij schuiven. Dat
 * ene vakje bij de as wordt na het spiegelen het paar dat je bij een isometrische
 * punt verwacht.
 *
 * `dy` is +1 voor omlaag en -1 voor omhoog.
 */
function ray(cx, y0, dy, w, h, cells) {
  for (let k = 0; ; k++) {
    const y = y0 + dy * k;
    const xs = k === 0 ? [cx] : [cx + 2 * k - 1, cx + 2 * k];
    if (xs[0] >= w) break;
    if (y >= 0 && y < h) for (const x of xs) if (x < w) cells.push([x, y]);
    // Doorlopen zolang er nog iets in beeld kan komen: een straal die boven het
    // doek begint moet er nog in kunnen zakken.
    if (dy > 0 ? y >= h : y < 0) break;
  }
}

/** De vakjes van de rechterhelft, plus hun spiegelbeeld, zonder dubbele. */
function mirror(cells, w, h) {
  const gezien = new Set();
  const uit = [];
  const zet = (x, y) => {
    if (x < 0 || y < 0 || x >= w || y >= h) return;
    const k = x + ',' + y;
    if (gezien.has(k)) return;
    gezien.add(k);
    uit.push([x, y]);
  };
  for (const [x, y] of cells) { zet(x, y); zet(w - 1 - x, y); }
  return uit;
}

/**
 * Hoe groot de kubus wordt, in stappen van de 2:1 lijn.
 *
 * Met `n` stappen is de bovenruit 4n-2 breed en 2n-1 hoog, en zijn de staande
 * ribben even lang als een halve ruitbreedte. Dan is het een kubus en geen tegel
 * of toren. Alles bij elkaar is hij 4n-2 hoog, net zo veel als hij breed is.
 */
function cubeSteps(w, h) {
  return Math.max(2, Math.min(7, Math.floor((w - 4) / 4) + 1, Math.floor((h - 4) / 4) + 1));
}

/**
 * Een isometrische kubus, midden op het doek.
 *
 * Een ruit van 2:1 als bovenkant, twee staande ribben aan de zijkanten en één in
 * het midden, en de onderrand ertussen. Precies de lijnen die je nodig hebt om een
 * blok te tekenen; vlakken vullen doe je zelf.
 *
 * De ribben zijn twee vakjes breed, want de punten van de ruit waar ze aan hangen
 * zijn dat ook. Eén vakje breed zou eronder wegvallen.
 */
export function cubeCells(w, h) {
  const cx = Math.round(w / 2);
  const n = cubeSteps(w, h);
  const rib = 2 * n - 1;                 // lengte van een staande rib
  const hoogte = 2 * (n - 1) + rib + 1;  // van de bovenpunt tot en met de onderpunt
  const top = Math.max(0, Math.round((h - hoogte) / 2));
  const half = [];

  // De ruit: de bovenrand, en dezelfde rand gespiegeld als onderrand.
  for (let k = 0; k < n; k++) {
    const xs = k === 0 ? [cx] : [cx + 2 * k - 1, cx + 2 * k];
    for (const x of xs) {
      half.push([x, top + k]);
      half.push([x, top + 2 * (n - 1) - k]);
      // En nog eens, een ribbe lager: dat is de onderrand van het blok.
      half.push([x, top + 2 * (n - 1) - k + rib]);
    }
  }

  // De rib aan de rechterkant, tussen de rechterpunt en de onderrand. Eén vakje
  // breed, en dan het buitenste: de punt van de ruit is twee vakjes breed, en een
  // rib die daar met twee vakjes onder hangt leest als een dubbele rand in plaats
  // van als de zijkant van een blok.
  const xr = cx + 2 * (n - 1);
  for (let y = top + n; y <= top + n - 1 + rib; y++) half.push([xr, y]);

  // En de rib in het midden, onder de onderpunt van de ruit.
  for (let y = top + 2 * (n - 1) + 1; y <= top + 2 * (n - 1) + rib; y++) half.push([cx, y]);

  return mirror(half, w, h);
}

/**
 * Een isometrisch raster over het hele doek: ruiten van 2:1, en niets anders.
 *
 * De staande lijnen die er eerst bij stonden zijn eruit. Die hoorden bij de kubus,
 * waar ze een ribbe zijn; in een raster waren het gewoon strepen die dwars door de
 * ruiten liepen en er niets aan toevoegden.
 *
 * `stap` is hoeveel rijen er tussen twee kruisingen zitten. Acht, en dat volgt uit
 * de schuinte: bundels van acht kruisen elkaar halverwege en vormen ruiten van 16
 * bij 8, de maat van een isometrische tegel. Met vier werd bijna de helft van het
 * doek blauw.
 */
export function isoCells(w, h, stap) {
  const s = stap || 8;
  const cx = Math.round(w / 2);
  const over = Math.ceil(w / 2);
  const half = [];
  for (let y = -over; y <= h + over; y += s) {
    ray(cx, y, 1, w, h, half);
    ray(cx, y, -1, w, h, half);
  }
  return mirror(half, w, h);
}

/**
 * De vakjes als één svg-pad: per vakje een blokje van 1 bij 1.
 *
 * Eén pad en niet honderden losse blokjes. Het raster raakt een paar honderd
 * vakjes, en dat zouden evenveel elementen zijn die de app bij elke hertekening
 * langs moet. Als pad is het één element en ziet het er precies hetzelfde uit.
 */
export function cellsToPath(cells) {
  let d = '';
  for (const [x, y] of cells) d += 'M' + x + ' ' + y + 'h1v1h-1Z';
  return d;
}
